import React, {useState} from 'react';
import styled from 'styled-components';
import {useTranslation} from 'react-i18next';
import Constants from '../../constants/Constants';
import {IReactionGroup} from '../../model/emojiReactionsModel';
import {getNativeEmojiById} from '../../util/EmojiSearch';
import withMemoizedContexts from '../../context/contextContainerHoC';
import {selectedReactionGroupStore, updateSelectedReactionGroupState} from '../../context/selectedReactionProvider';
import TimeFormatter from '../../util/TimeFormatter';
import {TscStyles} from '@techsmith/tsc-cloud-style-guide';

const ReactionMarker = styled.button<{width: number}>`
   display: flex;
   position: relative;
   background: none;
   border: none;
   outline: none;
   margin: 0;
   padding: 0;
   cursor: pointer;
   color: ${TscStyles.color.ui.dusk.mediumDark};
   width: ${props => props.width}px;
   height: 1rem;

   &::-moz-focus-inner {
      border: 0;
   }

   &:focus-visible {
      outline: none;

      &:after{
         content: '';
         display: block;
         position: absolute;
         top: -2px;
         right: -4px;
         bottom: -2px;
         left: -4px;
         border: 2px solid currentColor;
         border-radius: ${TscStyles.border.radius.md};
      }
   }
`;

const EmojiWrapper = styled.div<{leftOffset: number}>`
   display: inline-block;
   position: absolute;
   left: ${props => props.leftOffset}px;
`;

export const ReactionMarkerBase: React.FC<IReactionMarkerProps & IStateMappedProps> = ({reactionGroup, selectedReactionGroup}) => {
   const {t} = useTranslation();
   const [markerActiveOnMouseDown, setMarkerActiveOnMouseDown] = useState(false);
   const numberEmojiToDisplay = reactionGroup.reactions.length > Constants.reactionMarkers.maxNumberEmojiToPreview ? Constants.reactionMarkers.maxNumberEmojiToPreview : reactionGroup.reactions.length;
   const markerWidth = Constants.reactionMarkers.emojiWidth + (Constants.reactionMarkers.emojiOffset * (numberEmojiToDisplay - 1));

   const getEmojiPreview = (): React.ReactFragment => {
      const previewReactionList = reactionGroup.reactions.slice(reactionGroup.reactions.length - numberEmojiToDisplay, reactionGroup.reactions.length);
      return previewReactionList.map((reaction, index) => {
         const posIndex = numberEmojiToDisplay - (index + 1);
         return (
            <EmojiWrapper
               key={posIndex}
               leftOffset={posIndex * Constants.reactionMarkers.emojiOffset}
            >
               {getNativeEmojiById(reaction.EmojiId)}
            </EmojiWrapper>
         );
      });
   };

   const currentMarkerIsSelected = (): boolean => selectedReactionGroup && selectedReactionGroup.startTime === reactionGroup.startTime && selectedReactionGroup.endTime === reactionGroup.endTime;

   const onMarkerMouseDown = () => {
      // track if marker is active on mouse down to help prevent summary from popping up again on click
      // due the click off logic in the summary itself
      setMarkerActiveOnMouseDown(currentMarkerIsSelected());
   };

   const onMarkerClick = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      if (currentMarkerIsSelected() || markerActiveOnMouseDown) {
         setMarkerActiveOnMouseDown(false);
         updateSelectedReactionGroupState({selectedReactionGroup: null, selectedReactionMarkerButton: null});
      } else {
         updateSelectedReactionGroupState({selectedReactionGroup: reactionGroup, selectedReactionMarkerButton: e.target as HTMLButtonElement});
      }
   };

   return (
      <>
         <ReactionMarker
            width={markerWidth}
            aria-label={currentMarkerIsSelected() ? t('conversations.reactionMarkerHideAriaLabel', {time: TimeFormatter.formatTimeForSpeech(reactionGroup.startTime)}) : t('conversations.reactionMarkerShowAriaLabel', {time: TimeFormatter.formatTimeForSpeech(reactionGroup.startTime)})}
            onMouseDown={onMarkerMouseDown}
            onClick={onMarkerClick}
         >
            {getEmojiPreview()}
         </ReactionMarker>
      </>
   );
};

export interface IReactionMarkerProps {
   reactionGroup: IReactionGroup;
}

export interface IStateMappedProps {
   selectedReactionGroup: IReactionGroup;
}

const mapStatesToProps = (
   {reactionGroup}: IReactionMarkerProps,
   {selectedReactionGroup}: Partial<IStateMappedProps>
): IReactionMarkerProps & IStateMappedProps => ({reactionGroup, selectedReactionGroup});

export default withMemoizedContexts(mapStatesToProps, selectedReactionGroupStore)(ReactionMarkerBase);
